import React from 'react';
import { Link } from 'react-router-dom';
import { useUiTitle } from '../hooks/useUiTitle';
import { useOAuthProviders } from '../hooks/useOAuthProviders';
import { startOAuthLogin } from '../utils/startOAuthLogin';
import GradientBlobBackground from '../components/GradientBlobBackground/GradientBlobBackground';
import RetroGrid from '../components/RetroGrid/RetroGrid';
import FeatureExplorer from '../components/FeatureExplorer/FeatureExplorer';
import KineticText from '../components/KineticText/KineticText';
import robotMascot from '../assets/feature-slides/robot-mascot.png';
import logo from '../assets/logo.png';

// This project's own brand purple (tailwind.config.cjs primary-500/600),
// the same color already used for buttons across the real dashboard --
// not the generic periwinkle-blue from the reference design.
const ACCENT = '#8b5cf6';

// Robot mascot artwork Chris provided directly (transparent PNG), used as
// the actual visual asset instead of a hand-drawn SVG approximation.
const GatewayRobotIcon: React.FC<{ className?: string }> = ({ className }) => (
  <img src={robotMascot} alt="" className={className} draggable={false} />
);

const Landing: React.FC = () => {
  const uiTitle = useUiTitle();
  const { providers, authServerUrl, janusEnabled, loading } = useOAuthProviders();

  // With exactly one sign-in option configured overall, skip the /login
  // picker page entirely and go straight to it -- there's nothing to pick.
  // With zero or multiple options (or while still loading), fall through to
  // the normal /login navigation so the picker page can show the real
  // choices or the "no login methods configured" state. Janus counts as one
  // option alongside the static oauthProviders list, so a deployment with
  // Janus enabled and no oauth2_providers.yml entries still skips straight
  // to it, and one with both Janus and a static provider still shows the
  // picker rather than guessing which one the user wants.
  const totalOptions = providers.length + (janusEnabled ? 1 : 0);
  const handleSignInClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (loading || totalOptions !== 1) {
      return;
    }
    event.preventDefault();
    if (janusEnabled) {
      window.location.href = '/api/auth/auth/janus';
    } else {
      startOAuthLogin(providers[0].name, authServerUrl);
    }
  };

  return (
    <div className="relative z-0 w-full overflow-hidden" style={{ background: '#07070b', color: '#f5f5f7' }}>
      <div className="fixed inset-0 -z-10">
        <GradientBlobBackground accent={ACCENT} />
        <RetroGrid angle={65} cellSize={60} opacity={0.4} lineColor="#8b5cf6" />
      </div>

      <header className="relative z-10 flex items-center gap-4 flex-wrap px-6 py-7 max-w-[1280px] mx-auto">
        <div className="flex items-center gap-2.5">
          <img src={logo} alt="" className="h-8 w-8 brightness-0 invert flex-shrink-0" />
          <KineticText text={uiTitle} as="span" className="text-[15px] tracking-[0.2px]" />
        </div>
      </header>

      <main className="relative z-10 max-w-[1280px] mx-auto px-6 pt-16 pb-16">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          <div className="flex-1 min-w-0">
            <div
              className="inline-flex items-center gap-2 text-[12px] font-semibold tracking-wide uppercase px-3.5 py-1.5 rounded-full mb-6"
              style={{ color: '#c4b5fd', background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.3)' }}
            >
              One gateway for every MCP call
            </div>

            <h1 className="m-0 mb-5 font-bold leading-[1.1]" style={{ fontSize: 'clamp(32px,4vw,48px)' }}>
              Your MCP servers,
              <br />
              agents, and skills —
              <br />
              <span style={{ background: `linear-gradient(90deg,#c4b5fd,${ACCENT})`, WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>
                governed in one place
              </span>
            </h1>

            <p className="m-0 mb-8 text-base leading-relaxed max-w-[480px]" style={{ color: '#b7b2c9' }}>
              Register once, discover with semantic search, and let every call run under the signed-in person's own
              identity — with a full audit trail behind it.
            </p>

            <div className="flex gap-3.5 flex-wrap">
              <Link
                to="/login"
                onClick={handleSignInClick}
                className="gw-cta-primary text-white font-semibold rounded-[11px] px-7 py-3.5 inline-flex items-center gap-2"
                style={{ background: `linear-gradient(135deg,${ACCENT},#7c3aed)` }}
              >
                Sign In
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M5 12h14M13 6l6 6-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
              <button
                type="button"
                onClick={() => document.getElementById('feature-showcase')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="font-semibold rounded-[11px] px-7 py-3.5 inline-flex items-center gap-2"
                style={{ background: 'rgba(255,255,255,0.03)', color: '#f3f1fb', border: '1px solid rgba(255,255,255,0.14)' }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
                Explore Features
              </button>
            </div>
          </div>

          <div className="flex-1 min-w-0 flex justify-center">
            <div className="relative flex flex-col sm:flex-row items-center gap-5 sm:gap-3.5 w-full sm:w-auto max-w-full">
              <div
                className="absolute left-1/2 top-1/2 pointer-events-none"
                style={{ width: 460, height: 460, transform: 'translate(-50%,-50%)', background: `radial-gradient(circle, ${ACCENT}29, transparent 68%)`, filter: 'blur(16px)' }}
              />

              <GatewayRobotIcon className="gw-float relative flex-shrink-0 w-[104px] h-[104px] object-contain -mr-2" />

              <div className="relative flex flex-col gap-3 w-full sm:w-auto">
                <div className="hero-card flex items-center gap-2.5 rounded-xl px-4 py-3" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="flex-shrink-0"><rect x="4" y="4" width="16" height="16" rx="3" stroke="#c4b5fd" strokeWidth="1.7" /></svg>
                  <span className="text-[13px] font-semibold">MCP servers</span>
                </div>
                <div className="hero-card flex items-center gap-2.5 rounded-xl px-4 py-3" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="flex-shrink-0"><path d="M17 20v-2a4 4 0 00-4-4H7a4 4 0 00-4 4v2" stroke="#c4b5fd" strokeWidth="1.7" /><circle cx="10" cy="7" r="3.2" stroke="#c4b5fd" strokeWidth="1.7" /></svg>
                  <span className="text-[13px] font-semibold">Agents</span>
                </div>
                <div className="hero-card flex items-center gap-2.5 rounded-xl px-4 py-3" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="flex-shrink-0"><path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" stroke="#c4b5fd" strokeWidth="1.7" strokeLinejoin="round" /></svg>
                  <span className="text-[13px] font-semibold">Skills</span>
                </div>
              </div>

              <svg width="16" height="32" viewBox="0 0 16 32" aria-hidden="true" className="relative sm:hidden flex-shrink-0"><path d="M8 2v20" stroke={ACCENT} strokeWidth="2" strokeLinecap="round" style={{ filter: `drop-shadow(0 0 4px ${ACCENT})` }} /><path d="M2 18l6 8 6-8" stroke={ACCENT} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ filter: `drop-shadow(0 0 4px ${ACCENT})` }} /></svg>
              <svg width="32" height="16" viewBox="0 0 32 16" aria-hidden="true" className="relative hidden sm:block flex-shrink-0"><path d="M2 8h20" stroke={ACCENT} strokeWidth="2" strokeLinecap="round" style={{ filter: `drop-shadow(0 0 4px ${ACCENT})` }} /><path d="M18 2l8 6-8 6" stroke={ACCENT} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ filter: `drop-shadow(0 0 4px ${ACCENT})` }} /></svg>

              <div
                className="hero-gateway relative rounded-2xl p-6 flex flex-col items-center gap-2 flex-shrink-0"
                style={{ background: `linear-gradient(135deg,${ACCENT},#6d28d9)` }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" stroke="#fff" strokeWidth="1.8" strokeLinejoin="round" /></svg>
                <span className="text-[13px] font-bold text-white">Gateway</span>
              </div>

              <svg width="16" height="32" viewBox="0 0 16 32" aria-hidden="true" className="relative sm:hidden flex-shrink-0"><path d="M8 2v20" stroke={ACCENT} strokeWidth="2" strokeLinecap="round" style={{ filter: `drop-shadow(0 0 4px ${ACCENT})` }} /><path d="M2 18l6 8 6-8" stroke={ACCENT} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ filter: `drop-shadow(0 0 4px ${ACCENT})` }} /></svg>
              <svg width="32" height="16" viewBox="0 0 32 16" aria-hidden="true" className="relative hidden sm:block flex-shrink-0"><path d="M2 8h20" stroke={ACCENT} strokeWidth="2" strokeLinecap="round" style={{ filter: `drop-shadow(0 0 4px ${ACCENT})` }} /><path d="M18 2l8 6-8 6" stroke={ACCENT} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ filter: `drop-shadow(0 0 4px ${ACCENT})` }} /></svg>

              <div className="hero-card relative flex-shrink-0 rounded-xl px-4 py-4 flex flex-col items-center gap-1.5" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M17 20v-2a4 4 0 00-4-4H7a4 4 0 00-4 4v2" stroke="#c4b5fd" strokeWidth="1.7" /><circle cx="10" cy="7" r="3.2" stroke="#c4b5fd" strokeWidth="1.7" /></svg>
                <span className="text-[12px] font-semibold whitespace-nowrap">Your teams</span>
              </div>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        @keyframes gw-float { 0%,100%{transform:translateY(0);} 50%{transform:translateY(-8px);} }
        @keyframes hero-pulse { 0%,100%{box-shadow:0 0 0 1px rgba(255,255,255,0.15), 0 0 32px rgba(139,92,246,0.55), 0 14px 30px rgba(0,0,0,0.4);} 50%{box-shadow:0 0 0 1px rgba(255,255,255,0.22), 0 0 52px rgba(139,92,246,0.85), 0 14px 30px rgba(0,0,0,0.4);} }
        .gw-float{animation:gw-float 5s ease-in-out infinite;}
        .hero-gateway{animation:hero-pulse 2.8s ease-in-out infinite;}
        .hero-card{transition:transform .2s ease, border-color .2s ease, background .2s ease;}
        .hero-card:hover{transform:translateX(3px); border-color:rgba(196,181,253,0.4); background:rgba(255,255,255,0.08);}
        .gw-cta-primary{transition:transform .15s ease, box-shadow .15s ease;}
        .gw-cta-primary:hover{transform:translateY(-1px); box-shadow:0 14px 36px rgba(139,92,246,0.55), 0 0 0 1px rgba(196,181,253,0.3);}
        @media (prefers-reduced-motion: reduce) { .gw-float, .hero-gateway { animation: none !important; } }
      `}</style>

      <section id="feature-showcase" className="relative z-10 w-full px-6 py-20">
        <div className="text-center max-w-[640px] mx-auto mb-12 px-6">
          <h3 className="m-0 mb-4 font-bold leading-[1.1]" style={{ fontSize: 'clamp(30px,3.6vw,44px)' }}>
            Everything your teams need,
            <br />
            <span style={{ background: `linear-gradient(90deg,#c4b5fd,${ACCENT})`, WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>
              in one gateway
            </span>
          </h3>
          <p className="m-0 leading-relaxed" style={{ color: '#b7b2c9', fontSize: 'clamp(15px,1.3vw,18px)' }}>
            Unified access, discovery, and governance for every MCP server, agent, and skill your teams use.
          </p>
        </div>
        <div className="max-w-[1180px] mx-auto">
          <FeatureExplorer onSignInClick={handleSignInClick} />
        </div>
      </section>

      <footer
        className="relative z-10 text-center py-5 text-xs"
        style={{ color: '#6b7280', borderTop: '1px solid rgba(255,255,255,0.06)' }}
      >
        <span>{typeof window !== 'undefined' ? window.location.hostname : ''}</span>
      </footer>
    </div>
  );
};

export default Landing;
